import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "@/constants/testIds";

const links = [
  { id: "services", label: "Services", testid: NAV.linkServices },
  { id: "work", label: "Work", testid: NAV.linkWork },
  { id: "process", label: "Process", testid: NAV.linkProcess },
  { id: "contact", label: "Contact", testid: NAV.linkContact },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-nav py-3" : "bg-transparent py-5"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <button
          data-testid={NAV.logo}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="group inline-flex items-baseline gap-[3px] select-none"
        >
          <span className="font-display text-[20px] md:text-[22px] font-black tracking-[-0.04em] text-white leading-none">
            JD
          </span>
          <span className="font-display text-[20px] md:text-[22px] font-light tracking-[0.32em] text-white/85 leading-none uppercase">
            tech
          </span>
          <span className="ml-1 h-1.5 w-1.5 rounded-full bg-[#0A84FF] shadow-[0_0_12px_rgba(10,132,255,0.9)] group-hover:scale-125 transition-transform" />
        </button>

        <ul className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <li key={l.id}>
              <button
                data-testid={l.testid}
                onClick={() => go(l.id)}
                className="px-4 py-2 text-sm text-[#a1a1a6] hover:text-white transition-colors duration-200"
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="mailto:contact@jdtechnologies.co"
            data-testid={NAV.ctaContact}
            className="btn-glow inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-sm font-bold tracking-tight"
          >
            Start a project
            <ArrowUpRight size={16} strokeWidth={2.5} />
          </a>
        </div>

        <button
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden h-10 w-10 grid place-items-center rounded-full glass"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="md:hidden px-6 pb-6 pt-2"
          >
            <div className="glass rounded-2xl p-4 flex flex-col">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={l.testid}
                  onClick={() => go(l.id)}
                  className="text-left px-3 py-3 text-base text-white/90 hover:text-white border-b border-white/5 last:border-b-0"
                >
                  {l.label}
                </button>
              ))}
              <a
                href="mailto:contact@jdtechnologies.co"
                className="mt-3 inline-flex justify-center items-center gap-2 rounded-full bg-white text-black px-5 py-3 text-sm font-bold"
              >
                Start a project <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
